import { useEffect } from 'react';
import { reaction } from 'mobx';

import type { BrandingImageModel } from './BrandingImageModel';

/**
 * Wire a {@link BrandingImageModel} into the config modal's `useState` form
 * state.
 *
 * The modal predates the `src/edit/` stack: `prepareSave` is handed only
 * `formData`, so the model's value has to be pushed into it rather than read
 * out of the model at save time. Its own file because that push is the one part
 * of this editor that fails *silently* — with the reaction missing, the dialog
 * looks and behaves correctly and stores nothing.
 *
 * `fireImmediately` matters on both: an editor opened and closed without a
 * change must leave formData and the modal's validity exactly as it found them.
 */
export const useBrandingImageBridge = (
  model: BrandingImageModel,
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  setJsonValid: (valid: boolean) => void
): void => {
  useEffect(() => {
    const stopImage = reaction(
      () => model.saveValue.image,
      (image) => setFormData((previous: any) => ({ ...(previous ?? {}), image })),
      { fireImmediately: true }
    );
    // The server stays the validation authority for the value itself; this only
    // stops the modal saving a square it already knows it cannot store.
    const stopValid = reaction(
      () => model.isValid,
      (valid) => setJsonValid(valid),
      { fireImmediately: true }
    );
    return () => {
      stopImage();
      stopValid();
      model.dispose();
    };
  }, [model, setFormData, setJsonValid]);
};
